import { z } from 'zod';
import {
  AttendanceProfileSchema,
  EmployeeEnrollmentStatusSchema,
  EmploymentStatusSchema,
  WorkforceTypeSchema,
} from './employee.schema.js';
import { ErpStatusFilterShape } from './erpStatusFilter.schema.js';
import { WorkforceGroupQuerySchema } from '../filters/workforceGroup.js';

/**
 * WHAT THE ROSTER MAY ASK FOR.
 *
 * Every field here is a narrowing some screen sends, and each one has a twin
 * obligation on the server: `buildEmployeeListFilterWhere` must apply it, and
 * `WorkerSelectionFilterSchema` must be able to express it — a filter the list
 * applies and the bulk resolver cannot is a bulk action that silently reaches
 * people the reader never saw.
 */
export const EmployeeListQuerySchema = z.object({
  search: z.string().optional(),
  departmentId: z.string().optional(),
  employmentStatus: EmploymentStatusSchema.optional(),
  // Any-of Hadir statuses, the ERP code, and "set by HR" (erpStatusFilter.schema.ts).
  ...ErpStatusFilterShape,
  // Filter the list to a FIELD face-enrollment workflow state (e.g. the web
  // "pending enrollment" quick-filter passes NOT_ENROLLED).
  enrollmentStatus: EmployeeEnrollmentStatusSchema.optional(),
  // Bulk-worker filters (construction): position/trade and assignment scope.
  // campId/worksiteId match a WorksiteAssignment; designationId IS the
  // position/category (Employee.designation FK). Wired into the list
  // where-builder by the position-filters leaf — same spread-conditional
  // pattern as enrollmentStatus above.
  campId: z.string().optional(),
  worksiteId: z.string().optional(),
  designationId: z.string().optional(),
  // Branch (legal entity) + labour-supplier scope — drives "manage employees on
  // each branch" and the Branch report drill-down.
  legalEntityId: z.string().optional(),
  // The ERP payroll (owner, 2026-10-28) — `Employee.erpPayrollNumber`, the PRL
  // code the profile already shows as "ERP payroll group". Exact text: `003`.
  erpPayrollNumber: z.string().trim().max(10).optional(),
  supplierId: z.string().optional(),
  // THE WORKFORCE GROUP — Workers · Staff · Remote, the app's one rule
  // (filters/workforceGroup.ts, owner D1): the three split everyone cleanly.
  // Absent means everyone.
  group: WorkforceGroupQuerySchema.optional(),
  // The two params `group` replaced. Still accepted from an older client, and
  // read as the GROUP they spelled (`workforceGroupFromParams`), never as the
  // old column match — `workforceType=SITE_WORKER` is Workers, so a remote
  // site worker is not in it. An `attendanceProfile` other than SELF_ANYWHERE
  // is not a group and still narrows by the column.
  workforceType: WorkforceTypeSchema.optional(),
  attendanceProfile: AttendanceProfileSchema.optional(),
  // Exactly as stored — the options come from `/employees/nationalities`, which
  // reads the column, so a value sent back is one some row actually holds.
  nationality: z.string().max(80).optional(),
  // Not a narrowing but a PROJECTION: the org directory's minimal row, company
  // wide, for any caller (employee.list.ts). Without it a holder of
  // `employees.view` gets the roster row over their own scope only — which, for
  // a DIRECT_REPORTS manager, is a directory of their own team.
  view: z.enum(['directory']).optional(),
  sortBy: z
    .enum(['lastName', 'firstName', 'employeeCode', 'joiningDate', 'createdAt'])
    .optional(),
  sortDir: z.enum(['asc', 'desc']).optional(),
  page: z.coerce.number().int().positive().default(1),
  // Cap raised 200 → 500 so the schedule planner can request the full roster
  // for mid-size orgs in one call (SchedulePage requests limit:500). Larger
  // orgs still need real pagination — tracked as P2.
  limit: z.coerce.number().int().positive().max(500).default(20),
});
export type EmployeeListQuery = z.infer<typeof EmployeeListQuerySchema>;

/**
 * THE ROSTER AS A FILE (merge E4): the list's own query, plus the format and
 * the table's column keys (`columns=name,position,…`) — the server maps those
 * to file columns and drops any key the roster does not draw.
 */
export const EmployeeRosterFileQuerySchema = EmployeeListQuerySchema.extend({
  format: z.enum(['excel', 'csv']),
  columns: z.string().max(160).optional(),
});
export type EmployeeRosterFileQuery = z.infer<typeof EmployeeRosterFileQuerySchema>;
